import type { MapLocation } from "./DistributorMap";
import { getFilteredSkillIds } from "./distributor-type";
import { renderDistributorSkillIcon } from "./distributor-render-skill-icon";
import { renderAdditionalIconsForDistributor } from "./distributor-additional-icons";

/**
 * A function that when called returns the (hopefully) localized title for a skill id.
 */
type SkillTitleFetcher = (
  skillId: ReturnType<typeof getFilteredSkillIds>[number]
) => string;

/**
 * Renders the contents for the distributor map info window for a single location.
 *
 * @param location
 * the rendered location object
 * @param fetchSkillTitle
 * an optional function which will be used to retrieve the display title for a skill identifier
 * @return
 * the rendered dom element (still off-screen, use append on another element to make it visible)
 */
export const renderDistributorMapInfoWindow = (
  location: MapLocation,
  fetchSkillTitle: SkillTitleFetcher = (skillId) => skillId
): HTMLDivElement => {
  const wrapper = document.createElement("div");
  wrapper.classList.add("map__location");
  return appendOptionalElements(
    wrapper,
    renderLocationName(location.Name),
    renderSkills(location, fetchSkillTitle),
    appendOptionalElements(
      renderDivContainer(),
      renderGenericLocationInfo(location.Visit_Street__c),
      renderGenericLocationInfo(location.Visit_City__c),
      renderGenericLocationInfo(location.Phone),
      renderWebsiteLink(location.Website)
    ),
    renderAdditionalIconsForDistributor(location)
  );
};

const renderLocationName = (locationName: Partial<MapLocation>["Name"]) => {
  if (!locationName) {
    return null;
  }

  const textElement = document.createElement("strong");
  textElement.classList.add("font-size-s");
  textElement.append(locationName);

  const nameWrapper = document.createElement("div");
  nameWrapper.classList.add("map__info__headline");
  nameWrapper.append(textElement);
  return nameWrapper;
};

const renderSkills = (
  location: MapLocation,
  fetchSkillTitle: SkillTitleFetcher
) => {
  const skills = getFilteredSkillIds(location);

  const list = document.createElement("div");
  list.classList.add(
    "map__info__skills",
    "margin-top--xxs",
    "margin-bottom--xs"
  );

  for (const skillIdentifier of skills) {
    const skillTitle = fetchSkillTitle(skillIdentifier);

    const imageElement = renderDistributorSkillIcon(
      skillIdentifier,
      skillTitle
    );
    imageElement.title = skillTitle;

    const wrappingElement = document.createElement("div");
    wrappingElement.append(imageElement);

    wrappingElement.classList.add("map__info__skills__item");
    list.append(wrappingElement);
  }

  return list;
};

const renderGenericLocationInfo = (content?: string) => {
  if (!content) {
    return null;
  }

  const element = document.createElement("div");
  element.append(content);
  return element;
};

/**
 * Renders a given string of a website authority (without protocol) into a hyperlink.
 */
const renderWebsiteLink = (targetWebAuthority?: string) => {
  if (!targetWebAuthority) {
    return null;
  }

  const linkElement = document.createElement("a");
  const link = targetWebAuthority.replace(/(^\w+:|^)\/\//, '');
  linkElement.href = `//${link}`;
  linkElement.target = "_blank";
  linkElement.append(targetWebAuthority);

  const wrappingElement = document.createElement("div");
  wrappingElement.classList.add("padding-top--xs");
  wrappingElement.append(linkElement);
  return wrappingElement;
};

/**
 * Render a {@link HTMLDivElement} with the given content as children.
 */
export const renderDivContainer = (...classNames: string[]) => {
  const containerElement = document.createElement("div");
  containerElement.classList.add(...classNames);
  return containerElement;
};

/**
 * Appends all given notes to the target element, that are not undefined or null.
 */
export const appendOptionalElements = <TargetElement extends Element>(
  target: TargetElement,
  ...notesToAppend: (Node | string | undefined | null)[]
) => {
  target.append(
    ...notesToAppend
      .filter(<T>(e: T | undefined): e is T => e !== undefined)
      .filter(<T>(e: T | null): e is T => e !== null)
  );
  return target;
};
